import { isNull } from "lodash";
import { layerSort } from "utils/codes";
import {
    emissionGradeFromEmbC,
    getLayerEmbCarbonFieldKey,
    getMaterialEmissionsBenchmarkValue,
    getMaterialLayerEmissionGrade,
    storageGradeFromEmbC,
} from "../embodiedCarbon";
import {
    STANDARD_STEEL_STUD_CROSS_SECTION_AREA,
    UDEF_CONTINUOUS_MEDIUM_MATERIAL_TYPE,
    UDEF_INSULATION_MATERIAL_TYPE,
    UDEF_UNDEFINED_MATERIAL_TYPE,
    STEEL_EMBODIED_CARBON_PER_M_STEEL_STUD,
    getSteelFramingLengthPerM2,
} from "./EMBC_UDEF_MAPS";

import { WOOD_AVERAGES, DEFAULT_WOOD_PROVINCE_KEY } from "../embodiedCarbon";

/**
 * Resolve display values for LayerGrade (defaults + optional layer_embC raw overrides).
 * Basement wall uses `interiorFinish`, `insulationFramingLayer`, and `extraInsulationLayer` as layer keys (see templates).
 */

const MAP_BY_UDEF_LAYER = {
    continuousMedium: UDEF_CONTINUOUS_MEDIUM_MATERIAL_TYPE,
    continuousInsulation: UDEF_INSULATION_MATERIAL_TYPE,
    woodFraming: UDEF_INSULATION_MATERIAL_TYPE,
    steelFraming: UDEF_INSULATION_MATERIAL_TYPE,
    strapping: UDEF_INSULATION_MATERIAL_TYPE,
};

export const getUdefLayerMap = (layerType) => {
    return MAP_BY_UDEF_LAYER[layerType];
};

export const getUdefLayerEmbodiedCarbon = ({ layerType, layer }) => {
    const map = getUdefLayerMap(layerType);
    const codeStr = (layer?.materialType?.id ?? "-1").toString();

    const defaultRow = codeStr === "-1" ? UDEF_UNDEFINED_MATERIAL_TYPE : map?.[codeStr] || {};

    if (defaultRow?.perUnit === "m2/mm" && !isNull(defaultRow.emissions) && !isNull(defaultRow.storage)) {
        if (layer?.thickness) {
            return {
                ...defaultRow,
                emissions: defaultRow.emissions * layer.thickness,
                storage: defaultRow.storage * layer.thickness,
            };
        } else {
            return {
                ...defaultRow,
                emissions: 0,
                storage: 0,
            };
        }
    }
    return defaultRow;
};

export const isCavityInsulationLayer = (layerType) => {
    return ["woodFraming", "steelFraming", "strapping"].includes(layerType);
};

// Calculates wood volume (m³) per m² of wall face area from repeating vertical studs.
// Stud centre-to-centre spacing, face width, and cavity depth — all in mm (field base units).
// Doesn't include top and bottom plates or other studs for common framing practices

const getWoodFramingVolumeM3PerM2 = ({ spacing, width, thickness }) => {
    if (spacing == null || width == null || thickness == null) return 0;
    if (spacing <= 0 || width <= 0 || thickness <= 0) return 0;

    const spacingM = spacing / 1000;
    const widthM = width / 1000;
    const thicknessM = thickness / 1000;

    return (widthM * thicknessM) / spacingM;
};

//Gets the overhead factor from the building code so that stud-only wood volume can be converted to actual wood volume used in the wall
//accounts for top and bottom plates, headers, and other studs for common framing practices
//equation based on code values for typical wood-framed walls.
//Accepts spacing in mm
const getBuildingCodeFramingOverheadFactor = ({ spacing }) => {
    if (spacing == null || spacing <= 0) return 1;
    const overheadFactor = 1.7533 * Math.log(spacing) - 8.0748;

    if (overheadFactor < 1) return 1;
    return overheadFactor;
};

const getWoodFramingEmbodiedCarbonDisplay = ({ layer }) => {
    //Calculating wood framing embodied carbon based on spacing, thickness (depth), and width
    if (!layer) {
        return { emissions: 0, storage: 0, framingFraction: 0 };
    }
    const { spacing, thickness, width } = layer;
    if (spacing == null || width == null || thickness == null)
        return {
            emissions: 0,
            storage: 0,
            framingFraction: 0,
        };
    if (spacing <= 0 || width <= 0 || thickness <= 0)
        return {
            emissions: 0,
            storage: 0,
            framingFraction: 0,
        };

    const rawWoodAreaFraction = width / spacing;
    const overheadFactor = getBuildingCodeFramingOverheadFactor({ spacing });
    const actualWoodAreaFraction = rawWoodAreaFraction * overheadFactor;
    const actualWoodVolumeM3PerM2 = (thickness / 1000) * actualWoodAreaFraction;

    const woodAverage = WOOD_AVERAGES[DEFAULT_WOOD_PROVINCE_KEY];
    const emissions = actualWoodVolumeM3PerM2 * woodAverage.emissions;
    const storage = actualWoodVolumeM3PerM2 * woodAverage.storage;

    return {
        emissions: emissions,
        storage: storage,
        framingFraction: actualWoodAreaFraction, //fraction, not percentage, to be used for cavity calculations
    };
};

const getSteelFramingEmbodiedCarbonDisplay = ({ layer }) => {
    if (!layer) {
        return { emissions: 0, storage: 0, framingFraction: 0 };
    }
    const { spacing, thickness, width, steelGauge: { value: steelGaugeValue } = {} } = layer;
    if (spacing == null || width == null || thickness == null || steelGaugeValue == null)
        return {
            emissions: 0,
            storage: 0,
            framingFraction: 0,
        };
    if (spacing <= 0 || width <= 0 || thickness <= 0)
        return {
            emissions: 0,
            storage: 0,
            framingFraction: 0,
        };

    // const spacingM = spacing / 1000; //Not used
    const widthM = width / 1000;
    const thicknessM = thickness / 1000;
    const steelGaugeM = steelGaugeValue / 1000;

    //Calculate cross section area of the steel stud
    const crossSectionArea = steelGaugeM * (thicknessM + 2 * widthM + 2 * (12.7 / 1000));

    const steelStudRatio = crossSectionArea / STANDARD_STEEL_STUD_CROSS_SECTION_AREA;

    const steelStudLengthPerM2 = getSteelFramingLengthPerM2(spacing);

    const steelStudEmissions = STEEL_EMBODIED_CARBON_PER_M_STEEL_STUD * steelStudLengthPerM2 * steelStudRatio;

    const framingFraction = spacing < 500 ? 0.77 / 100 : 0.67 / 100;

    return {
        emissions: steelStudEmissions,
        storage: 0,
        framingFraction: framingFraction,
    };
};

//This function mirrors getUdefLayerEmbodiedCarbon, but doesn't return the whole defaultRow object because we get info about grades from the cavity layer
export const getUdefFramingLayerEmbodiedCarbon = ({ layerType, layer }) => {
    if (layerType === "woodFraming") {
        return getWoodFramingEmbodiedCarbonDisplay({
            layer,
        });
    } else if (layerType === "steelFraming") {
        return getSteelFramingEmbodiedCarbonDisplay({
            layer,
        });
    } else if (layerType === "strapping") {
        return getWoodFramingEmbodiedCarbonDisplay({
            layer,
        });
    }

    return {
        emissions: 0,
        storage: 0,
        framingFraction: 0,
    };
};

/** User EPD override for a framing+cavity assembly, stored on the root layer (legacy: cavityInsulation). */
export const getUdefFramingAssemblyEmbC = (layer, layerType) => {
    const embKey = getUdefLayerEmbCarbonFieldKey(layerType);
    const rootEmbC = layer?.[embKey];
    if (rootEmbC?.emissions != null) return rootEmbC;
    return layer?.cavityInsulation?.[embKey];
};

export function getUdefFramingAssemblySnapshotForEmbC({ layerType, layer }) {
    const cavityLayer = layer?.cavityInsulation ?? layer;
    const framingLayer = layerType === "strapping" ? layer : layer?.framing;
    return {
        layerCodeId: cavityLayer?.materialType?.id,
        spacing: framingLayer?.spacing,
        width: framingLayer?.width,
        thickness: framingLayer?.thickness,
        steelGaugeValue: framingLayer?.steelGauge?.value,
    };
}

const udefFramingAssemblyEmbodiedMismatch = (layer, layerType, embC) => {
    if (!embC || embC.emissions == null) return false;
    const cur = getUdefFramingAssemblySnapshotForEmbC({ layerType, layer });
    return (
        String(embC.layerCodeId ?? "") !== String(cur.layerCodeId ?? "") ||
        String(embC.spacing ?? "") !== String(cur.spacing ?? "") ||
        String(embC.width ?? "") !== String(cur.width ?? "") ||
        String(embC.thickness ?? "") !== String(cur.thickness ?? "") ||
        String(embC.steelGaugeValue ?? "") !== String(cur.steelGaugeValue ?? "")
    );
};

/** Default kg/m² for the full framing + cavity assembly (no user EPD override). */
export const getUdefFramingAssemblyCalculated = ({ layerType, layer }) => {
    if (!layer) {
        return { emissions: null, storage: null, emissionGrade: "N/A", storageGrade: "N/A", defaultSource: "" };
    }

    const cavityLayer = layer.cavityInsulation ?? layer;
    const framingLayer = layerType === "strapping" ? layer : layer.framing;

    const cavityRow = getUdefLayerEmbodiedCarbon({ layerType, layer: cavityLayer });
    const initCavityEmissions = cavityRow.emissions ?? 0;
    const initCavityStorage = cavityRow.storage ?? 0;

    const {
        emissions: framingEmissions = 0,
        storage: framingStorage = 0,
        framingFraction = 0,
    } = getUdefFramingLayerEmbodiedCarbon({ layerType, layer: framingLayer });

    const emissions = initCavityEmissions * (1 - framingFraction) + framingEmissions;
    const storage = initCavityStorage * (1 - framingFraction) + framingStorage;

    const materialBench = getMaterialEmissionsBenchmarkValue(layerType);
    const emissionGrade =
        materialBench != null && emissions != null
            ? getMaterialLayerEmissionGrade(emissions, materialBench)
            : cavityRow.emissionGrade;

    return {
        emissions,
        storage,
        emissionGrade,
        storageGrade: cavityRow.storageGrade,
        defaultSource: cavityRow.source || "",
    };
};

export const getUdefLayerEmbodiedCarbonDisplay = ({ layerType, layer }) => {
    const defaultRow = getUdefLayerEmbodiedCarbon({ layerType, layer });

    const mapRowForDisplay = defaultRow;
    const embKey = getUdefLayerEmbCarbonFieldKey(layerType);
    const embC = layer?.[embKey];

    var emissions = mapRowForDisplay.emissions;
    var storage = mapRowForDisplay.storage;

    const hasMismatch =
        embC != null &&
        embC.emissions != null &&
        String(embC.layerCodeId ?? "") !== String(layer?.materialType?.id ?? "");

    if (embC != null && embC.emissions != null) {
        emissions = embC.emissions;
        storage = embC.storage ?? 0;

        return {
            emissions,
            storage,
            emissionGrade: emissionGradeFromEmbC(layerType, embC.emissions, mapRowForDisplay.emissions, emissions),
            storageGrade: storageGradeFromEmbC(embC.storage, mapRowForDisplay.storage, mapRowForDisplay),
            defaultSource: mapRowForDisplay.source || "",
            hasMismatch,
            mismatchCodeLabel: embC.layerCodeId,
        };
    }

    const materialBench = getMaterialEmissionsBenchmarkValue(layerType);
    const emissionGradeFromBenchmark =
        materialBench != null && emissions != null ? getMaterialLayerEmissionGrade(emissions, materialBench) : null;

    return {
        emissions: mapRowForDisplay.emissions,
        storage: mapRowForDisplay.storage,
        emissionGrade: emissionGradeFromBenchmark ?? mapRowForDisplay.emissionGrade, // TODO
        storageGrade: mapRowForDisplay.storageGrade, // TODO
        defaultSource: mapRowForDisplay.source || "",
        hasMismatch: false,
        mismatchCodeLabel: undefined,
    };
};

export const getUdefFramingAdjustedEmbodiedCarbonDisplay = ({ layerType, layer }) => {
    if (!layer) {
        return {
            emissions: null,
            storage: null,
            emissionGrade: "N/A",
            storageGrade: "N/A",
            hasMismatch: false,
        };
    }

    const calculated = getUdefFramingAssemblyCalculated({ layerType, layer });
    const assemblyEmbC = getUdefFramingAssemblyEmbC(layer, layerType);

    if (assemblyEmbC?.emissions != null) {
        return {
            emissions: assemblyEmbC.emissions,
            storage: assemblyEmbC.storage ?? 0,
            emissionGrade: emissionGradeFromEmbC(
                layerType,
                assemblyEmbC.emissions,
                calculated.emissions,
                assemblyEmbC.emissions
            ),
            storageGrade: storageGradeFromEmbC(assemblyEmbC.storage, calculated.storage, {
                storageGrade: calculated.storageGrade,
            }),
            defaultSource: calculated.defaultSource,
            hasMismatch: udefFramingAssemblyEmbodiedMismatch(layer, layerType, assemblyEmbC),
            mismatchCodeLabel: assemblyEmbC.layerCodeId,
        };
    }

    return {
        ...calculated,
        hasMismatch: false,
        mismatchCodeLabel: undefined,
    };
};

export function getWallLayersSnapshotForUdefEmbC(layers) {
    if (!layers) return {};
    return {
        layerCodeId: layers.materialType?.id,
    };
}

/** Flatten nested uDefCode.layers (from model codes) into the sorted array used by getUdefEmbodiedCarbon. */
export function getUdefStandardCodeLayersArray(codeData = {}) {
    const {
        continuousMedium = {},
        continuousInsulation = {},
        woodFraming = {},
        steelFraming = {},
        strapping = {},
    } = codeData.layers || {};

    const medium = Object.keys(continuousMedium).map((key) => ({
        ...continuousMedium[key],
        id: key,
        type: "continuousMedium",
    }));
    const insulation = Object.keys(continuousInsulation).map((key) => ({
        ...continuousInsulation[key],
        id: key,
        type: "continuousInsulation",
    }));
    const woodFramingLayers = Object.keys(woodFraming).map((key) => ({
        ...woodFraming[key],
        id: key,
        type: "woodFraming",
    }));
    const steelFramingLayers = Object.keys(steelFraming).map((key) => ({
        ...steelFraming[key],
        id: key,
        type: "steelFraming",
    }));
    const strappingLayers = Object.keys(strapping).map((key) => ({
        ...strapping[key],
        id: key,
        type: "strapping",
    }));

    return [...medium, ...insulation, ...woodFramingLayers, ...steelFramingLayers, ...strappingLayers].sort(layerSort);
}

const getUdefLayerEmbodiedDisplay = (layer) => {
    if (isCavityInsulationLayer(layer.type)) {
        return getUdefFramingAdjustedEmbodiedCarbonDisplay({
            layerType: layer.type,
            layer,
        });
    }
    return getUdefLayerEmbodiedCarbonDisplay({
        layerType: layer.type,
        layer,
    });
};

const udefLayerBreakdownKey = (layer) => `${layer.type}`;

/**
 * Per-layer embodied intensities (kg CO₂e/m²) keyed as `{layerType}.{layerId}`.
 */
export function getUdefEmbodiedCarbonLayerBreakdown({ layers }) {
    if (!layers?.length) return {};

    const breakdown = {};
    let totalEmissions = 0;
    let totalStorage = 0;

    for (const layer of layers) {
        const { emissions: layerEmissions = 0, storage: layerStorage = 0 } = getUdefLayerEmbodiedDisplay(layer);

        breakdown[udefLayerBreakdownKey(layer)] = {
            emissions: layerEmissions,
            storage: layerStorage,
        };

        totalEmissions += layerEmissions;
        totalStorage += layerStorage;
    }

    Object.keys(breakdown).forEach((key) => {
        const layerData = breakdown[key];
        layerData.emissionPercentage =
            totalEmissions > 0 ? Math.round(10000 * ((layerData.emissions ?? 0) / totalEmissions) * 100) / 10000 : 0;
        layerData.storagePercentage =
            totalStorage > 0 ? Math.round(10000 * ((layerData.storage ?? 0) / totalStorage) * 100) / 10000 : 0;
    });

    return breakdown;
}

export function getUdefEmbodiedCarbon({ layers }) {
    if (!layers) return {};

    let emissions = 0;
    let storage = 0;

    for (const layer of layers) {
        const { emissions: layerEmissions = 0, storage: layerStorage = 0 } = getUdefLayerEmbodiedDisplay(layer);
        emissions += layerEmissions;
        storage += layerStorage;
    }

    return {
        totalEmissions: emissions,
        totalStorage: storage,
    };
}

export const UDEF_LAYER_EMB_C_SUFFIX = "_embC";

export const getUdefLayerEmbCarbonFieldKey = (layerType) => `${layerType}${UDEF_LAYER_EMB_C_SUFFIX}`;
